import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Container, Row, Col, Card, Form, Button, Spinner, Badge } from 'react-bootstrap';
import api from '../../service/api.js';
import { useToast } from '../../contexts/ToastContext';
import { RiEyeLine, RiEyeOffLine, RiInformationLine, RiLockLine, RiMailLine, RiPhoneLine, RiSaveLine, RiShieldLine, RiUserLine } from 'react-icons/ri';

const Profile = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.profile) {
      setProfile({
        firstName: user.profile.firstName || '',
        lastName: user.profile.lastName || '',
        phone: user.profile.phone || ''
      });
    }
  }, [user]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put('/api/users/profile', { profile });
      showSuccess('Profile updated successfully!');
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await api.put('/api/users/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      showSuccess('Password changed successfully!');

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((current) => ({ ...current, [field]: !current[field] }));
  };

  return (
    <div className="profile-settings-page min-vh-100 py-4 px-3">
      <Container>

        <div className="mb-2 d-flex justify-content-between align-items-end">
          <div>
            <Badge bg="primary" className="bg-opacity-10 text-primary px-3 py-2 rounded-pill mb-3 fw-bold ls-wide">
              <RiUserLine className="me-2" aria-hidden="true" />USER ACCOUNT
            </Badge>
            <h1 className="fw-bold text-dark display-6 mb-0">Profile Settings</h1>
            <p className="text-secondary mt-2">Manage your personal information and security preferences</p>
          </div>
        </div>


        

        <Row className="g-3 g-lg-4">

          <Col md={7}>
            <Card className="profile-card border-0 shadow-sm overflow-hidden h-100">
              <Card.Header className="profile-card-header bg-white border-bottom">
                <span className="profile-card-icon"><RiUserLine aria-hidden="true" /></span>
                <div>
                  <h2>Personal Information</h2>
                  <p>Update your basic account details</p>
                </div>
              </Card.Header>
              <Card.Body className="profile-card-body">
                <Form onSubmit={handleProfileSubmit}>
                  <Row className="g-3 g-lg-4">
                    <Col lg={6}>
                      <Form.Group>
                        <Form.Label>Username</Form.Label>
                        <div className="profile-input-shell is-disabled">
                          <span className="profile-input-icon"><RiUserLine aria-hidden="true" /></span>
                          <Form.Control type="text" value={user?.username || ''} disabled className="profile-control" />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col lg={6}>
                      <Form.Group>
                        <Form.Label>Email Address</Form.Label>
                        <div className="profile-input-shell is-disabled">
                          <span className="profile-input-icon"><RiMailLine aria-hidden="true" /></span>
                          <Form.Control type="email" value={user?.email || ''} disabled className="profile-control" />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col lg={6}>
                      <Form.Group>
                        <Form.Label>First Name</Form.Label>
                        <div className="profile-input-shell">
                          <span className="profile-input-icon"><RiUserLine aria-hidden="true" /></span>
                          <Form.Control type="text" placeholder="Your first name" value={profile.firstName} onChange={(e) => setProfile({ ...profile, firstName: e.target.value })} className="profile-control" />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col lg={6}>
                      <Form.Group>
                        <Form.Label>Last Name</Form.Label>
                        <div className="profile-input-shell">
                          <span className="profile-input-icon"><RiUserLine aria-hidden="true" /></span>
                          <Form.Control type="text" placeholder="Your last name" value={profile.lastName} onChange={(e) => setProfile({ ...profile, lastName: e.target.value })} className="profile-control" />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Phone Number</Form.Label>
                        <div className="profile-input-shell">
                          <span className="profile-input-icon"><RiPhoneLine aria-hidden="true" /></span>
                          <Form.Control type="tel" placeholder="e.g. +1 234 567 890" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="profile-control" />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="profile-submit-row">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="profile-submit-button d-flex align-items-center justify-content-center gap-2"
                      variant="primary"
                    >
                      {loading ? <Spinner animation="border" size="sm" /> : <><RiSaveLine aria-hidden="true" /> Update Profile</>}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>


          <Col md={5}>
            <div className="d-flex flex-column gap-4 h-100">

              <Card className="profile-card border-0 shadow-sm overflow-hidden h-100">
                <Card.Header className="profile-card-header bg-white border-bottom">
                  <span className="profile-card-icon"><RiShieldLine aria-hidden="true" /></span>
                  <div>
                    <h2>Security</h2>
                    <p>Change your account password</p>
                  </div>
                </Card.Header>
                <Card.Body className="profile-card-body">
                  <Form onSubmit={handlePasswordSubmit}>
                    <Form.Group className="profile-password-group">
                      <Form.Label>Current Password</Form.Label>
                      <div className="profile-input-shell">
                        <span className="profile-input-icon"><RiLockLine aria-hidden="true" /></span>
                        <Form.Control type={passwordVisibility.currentPassword ? 'text' : 'password'} placeholder="••••••••" value={passwordData.currentPassword} onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })} required className="profile-control" />
                        <button type="button" className="profile-visibility-button" onClick={() => togglePasswordVisibility('currentPassword')} aria-label={passwordVisibility.currentPassword ? 'Hide current password' : 'Show current password'} aria-pressed={passwordVisibility.currentPassword}>
                          {passwordVisibility.currentPassword ? <RiEyeLine aria-hidden="true" /> : <RiEyeOffLine aria-hidden="true" />}
                        </button>
                      </div>
                    </Form.Group>
                    <Form.Group className="profile-password-group">
                      <Form.Label>New Password</Form.Label>
                      <div className="profile-input-shell">
                        <span className="profile-input-icon"><RiLockLine aria-hidden="true" /></span>
                        <Form.Control type={passwordVisibility.newPassword ? 'text' : 'password'} placeholder="New secret key" value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} required minLength="6" className="profile-control" />
                        <button type="button" className="profile-visibility-button" onClick={() => togglePasswordVisibility('newPassword')} aria-label={passwordVisibility.newPassword ? 'Hide new password' : 'Show new password'} aria-pressed={passwordVisibility.newPassword}>
                          {passwordVisibility.newPassword ? <RiEyeLine aria-hidden="true" /> : <RiEyeOffLine aria-hidden="true" />}
                        </button>
                      </div>
                      <Form.Text className="profile-password-hint">
                        <RiInformationLine aria-hidden="true" /> Use 6-32 characters
                      </Form.Text>
                    </Form.Group>
                    <Form.Group className="profile-password-group">
                      <Form.Label>Confirm New Password</Form.Label>
                      <div className="profile-input-shell">
                        <span className="profile-input-icon"><RiLockLine aria-hidden="true" /></span>
                        <Form.Control type={passwordVisibility.confirmPassword ? 'text' : 'password'} placeholder="Repeat new password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} required className="profile-control" />
                        <button type="button" className="profile-visibility-button" onClick={() => togglePasswordVisibility('confirmPassword')} aria-label={passwordVisibility.confirmPassword ? 'Hide confirmation password' : 'Show confirmation password'} aria-pressed={passwordVisibility.confirmPassword}>
                          {passwordVisibility.confirmPassword ? <RiEyeLine aria-hidden="true" /> : <RiEyeOffLine aria-hidden="true" />}
                        </button>
                      </div>
                    </Form.Group>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="profile-submit-button w-100 d-flex justify-content-center align-items-center gap-2"
                      variant="primary"
                    >
                      {loading ? <Spinner animation="border" size="sm" /> : <><RiShieldLine aria-hidden="true" /> Update Password</>}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>


            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Profile;

